import React, { useRef, useEffect, useState } from "react";
import { unset, get, set, pull, cloneDeep } from "lodash";
import ErrorBoundary from "./ErrorBoundary";
import Editor, { loader } from "@monaco-editor/react";
import lcSchema from "../../schema/legacyConfig.json";

const MIGRATED_SECTIONS = [
  "config.brands",
  "config.retailers",
  "config.search.categories",
  "config.manufacturer",
  "config.taxonomy.filters.category",
  "config.taxonomy.filters.subCategory",
  "config.taxonomy.limits",
  "config.multiLocationAvailability",
  "config.skuGroups",
  "config.bannersConfig",
  "config.clientStatus",
  "config.useGraph",
  "view.retailers",
  "icon",
  "type",
  "country",
  "organisationId",
  "multiConfigLabel",
  "identityProvider",
  "name",
  "features",
  "config.lookerConfig",
  "config.competitorList",
  "config.locationGroups"
];

const stripSchema = (schema) => {
  const newSchema = schema;

  MIGRATED_SECTIONS.forEach(ms => {
    let path = ms.split(".");
    const lastKey = path.pop();
    if (path.length > 0) {
      path = path.join(".properties.");
      path = `properties.${path}.required`;
    } else {
      path = "required";
    }
    let newReqirements = get(newSchema, path); 
    pull(newReqirements, lastKey);
    set(newSchema, path, newReqirements);
  });

  return newSchema
};

const Button = (props) => {
  let classes = "inline-flex items-center px-6 py-3 border border-transparent text-base	font-bold rounded-md "
  if (props.disabled) {
    classes = classes + "text-red-700 bg-red-100 opacity-50 cursor-not-allowed"
  } else {
    classes = classes + "text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
  }

  return (
    <button
      onClick={() => props.onClick()}
      className={classes}
      disabled={props.disabled}
    >
      {props.children}
    </button>
  );
}

const LegacyEditor = ({ clientConfig, preLoad = false, rawConfig = false }) => {
  const [strippedSections, setStrippedSections] = useState({});
  const [strippedConfig, setStrippedConfig] = useState(clientConfig);
  const [errors, setErrors] = useState([]);
  const [loaded, setLoaded] = useState(preLoad);
  const editorRef = useRef(null);

  useEffect(() => {
    const json = JSON.stringify(rawConfig ? lcSchema : stripSchema(lcSchema));
    const blob = new Blob([json], { type: "application/json" });
    const uri = URL.createObjectURL(blob);

    if (!rawConfig) {
      const conf = cloneDeep(clientConfig);
      const sections = {};

      MIGRATED_SECTIONS.forEach((ms) => {
        const value = get(conf, ms);
        set(sections, ms, value);

        unset(conf, ms);
      });

      setStrippedSections(sections);
      setStrippedConfig(conf); 
    }

    loader
      .init()
      .then((m) => {
        m.languages.json.jsonDefaults.setDiagnosticsOptions({
          validate: true,
          enableSchemaRequest: true,
          schemas: [{
            uri,
            fileMatch: ['*'],
            lcSchema
          }]
        });
      })
      .catch(error =>
        console.error("An error occurred during initialization of Monaco: ", error)
      );
  }, []);

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    const model = editor.getModel();

    editor.onDidChangeModelDecorations(() => {
      if (model === null || model.getLanguageId() !== "json")
        return;

      const owner = model.getLanguageId();
      const markers = monaco.editor.getModelMarkers({ owner });
      const errs = markers || [];
      if (editor.getValue().length < 10) {
        errs.push({ message: "Need some JSON here!" });
      }
      setErrors(errs);
    });

    setTimeout(() => setLoaded(true), 2000);
  };

  const mergeSections = (newConfig) => {
    const conf = newConfig;

    MIGRATED_SECTIONS.forEach((ms) => {
      const val = get(strippedSections, ms);
      set(conf, ms, val);
    });

    return conf;
  };

  const handleSave = () => {
    const editorValue = editorRef.current.getValue();
    let editedConfig = JSON.parse(editorValue);
    editedConfig = mergeSections(editedConfig);

    const el = document.querySelector("input[id*=payload]");
    el.value = rawConfig ? editorValue : JSON.stringify(editedConfig);
    document.querySelector(".form-submit").click();
  };

  const options = {
    mouseWheelZoom: true,
    smoothScrolling: true,
    autoClosingQuotes: false,
    autoIndent: "none",
    formatOnPaste: true,
    colorDecorators: true,
    cursorBlinking: "expand"
  };

  const hasErrors = errors.length > 0;

  return (
    <ErrorBoundary>
      <div className="bg-white">
        {loaded ?
          <div className="flex items-center" id="control-bar">
            <Button onClick={() => handleSave()} disabled={hasErrors}>Save</Button>
            <div className={"ml-4 " + (hasErrors ? "text-red-600" : "text-green-400")}>
              <span className="font-bold">{hasErrors ? `Error 1 of ${errors.length}` : "No Errors:"}: </span>
              <span>{hasErrors ? errors[0].message : "Saving available!"}</span>
            </div>
          </div> :
          <div className="animate-pulse flex items-center justify-center pt-3 pb-3">
            <p className="italic font-lg text-indigo-600">Loading Controls...</p>
          </div>
        }
        <Editor
          height="82vh"
          defaultLanguage="json"
          defaultValue={JSON.stringify(strippedConfig, null, 2)}
          onMount={handleEditorDidMount}
          options={options}
        />
      </div>
    </ErrorBoundary>
  );
}

export default LegacyEditor;
